import type { Membership, MembershipRole } from "../../../lib/queries/memberships";
import { isBlankNewRow } from "./draftValidation";
import { FIELD_LABELS } from "./draftFields";
import { identityLocked, mergeDuplicate, rebaseDraft, type RebaseInput } from "./draftRebase";
import { restoredRows, type DraftPayload } from "./draftPersist";
import { blankRow, persistedRow, rosterSignature, transferRow } from "./draftRows";
import { dayjs } from "@marekl/adminer/datetime";
import {
  emptyPerson,
  type DraftFieldPath,
  type DraftPerson,
  type DraftRow,
  type DraftState,
  type RebaseNotice,
  type RowErrors,
} from "./draftTypes";
import type { ID } from "../../../lib/id";

export { personToDraft, rosterSignature } from "./draftRows";

export const initialDraft: DraftState = {
  rows: [],
  sourceSeasonId: null,
  hydrated: false,
  leaderPersonId: null,
  rosterSignature: "",
  nextNewId: 1,
  formMessage: null,
  rebaseNotices: [],
};

export type DraftAction =
  | { type: "HYDRATE"; members: Membership[]; leaderPersonId: ID | null }
  | { type: "SET_LEADER"; leaderPersonId: ID | null }
  | { type: "SET_SOURCE_SEASON"; seasonId: ID | null }
  | { type: "TOGGLE_TRANSFER"; membership: Membership; leaderPersonId: ID | null }
  | { type: "SET_ALL_TRANSFERS"; candidates: Membership[]; on: boolean; leaderPersonId: ID | null }
  | { type: "RESET" }
  | { type: "SET_CELL"; key: string; field: keyof DraftPerson; value: string | DraftPerson["birth_date"] }
  | { type: "SET_ROLES"; key: string; roles: MembershipRole[] }
  | { type: "TOGGLE_REMOVE"; key: string }
  | {
      type: "APPLY_SERVER_ERRORS";
      byRow: Map<string, RowErrors>;
      message: string | null;
      /** Fields the server refused as changed on both sides, with its value (parseRowErrors). */
      conflicts?: Map<string, Record<string, string>>;
      /** admin / members_admin: may keep their own value on a filled identity field. */
      canEditIdentity?: boolean;
    }
  | { type: "CLEAR_FORM_MESSAGE" }
  /** Merge the draft with the roster as the server has it now (draftRebase). */
  | ({ type: "REBASE" } & RebaseInput)
  /** Open a saved rozpracovaná registrace: its rows, merged with the roster as it is now. */
  | ({ type: "RESTORE"; payload: DraftPayload } & Omit<RebaseInput, "known">)
  | MergeAction;

/**
 * What a merged row offers its reader, as DraftRoster raises it. The page
 * adds what the roster does not know (the active season, who is reading)
 * before dispatching.
 */
export type RosterMergeAction =
  | { type: "RESOLVE_CONFLICT"; key: string; field: keyof DraftPerson; take: "mine" | "theirs" }
  | { type: "REREGISTER"; key: string }
  | { type: "MERGE_DUPLICATE"; key: string }
  | { type: "KEEP_SEPARATE"; key: string }
  | { type: "DROP_STAGED"; key: string };

type MergeAction =
  | Exclude<RosterMergeAction, { type: "REREGISTER" | "MERGE_DUPLICATE" }>
  /** An orphaned row is registered again, as a transfer from the active season. */
  | { type: "REREGISTER"; key: string; seasonId: ID }
  | { type: "MERGE_DUPLICATE"; key: string; canEditIdentity: boolean }
  | { type: "CLEAR_REBASE_NOTICES" };



/** Clears the error on a field the moment the user edits it. */
function withoutError(errors: RowErrors, path: DraftFieldPath): RowErrors {
  if (!(path in errors)) return errors;
  const next = { ...errors };
  delete next[path];
  return next;
}

/**
 * A transfer staged from the chosen source season. A row the merge put back
 * on the soupiska (REREGISTER) is a transfer too, but from the ACTIVE season,
 * and switching the source must not take it away.
 */
function isSourceTransfer(r: DraftRow): boolean {
  return r.origin.kind === "transfer" && !r.origin.reregistered;
}

/**
 * A field the server refused as changed on both sides becomes a conflict on
 * the row itself, with the server's value, and that value becomes the field's
 * baseline: whichever side the leader then picks is sent against what is
 * stored, so the next submit is not refused again. Needs no roster copy of
 * the person, which a transfer row or one registered again does not have.
 */
function withServerConflicts(
  r: DraftRow,
  conflicts: Record<string, string> | undefined,
  canEditIdentity: boolean,
): { row: DraftRow; tookTheirs: (keyof DraftPerson)[] } {
  if (!conflicts || !r.baseline) return { row: r, tookTheirs: [] };
  const next = {
    ...r,
    person: { ...r.person },
    conflicts: { ...r.conflicts },
    baseline: { ...r.baseline },
    serverErrors: { ...r.serverErrors },
  };
  const tookTheirs: (keyof DraftPerson)[] = [];
  for (const [f, theirs] of Object.entries(conflicts)) {
    if (!(f in emptyPerson)) continue;
    const field = f as keyof DraftPerson;
    const value = field === "birth_date" ? (theirs ? dayjs(theirs) : null) : theirs;
    next.baseline = { ...next.baseline, [field]: theirs };
    delete next.serverErrors[`person.${field}` as DraftFieldPath];
    // A filled identity field this reader may not overwrite is no choice:
    // „Ponechat moje" would only be refused again by the identity lock.
    // The server's value stands, as it does when the merge finds it.
    if (identityLocked(field, r, { ...r.person, [field]: value } as DraftPerson, canEditIdentity)) {
      next.person = { ...next.person, [field]: value } as DraftPerson;
      delete next.conflicts[field];
      tookTheirs.push(field);
      continue;
    }
    next.conflicts[field] = { theirs: value };
  }
  return { row: next, tookTheirs };
}

function withoutConflict(conflicts: DraftRow["conflicts"], field: keyof DraftPerson): DraftRow["conflicts"] {
  if (!(field in conflicts)) return conflicts;
  const next = { ...conflicts };
  delete next[field];
  return next;
}

function mapRow(state: DraftState, key: string, fn: (r: DraftRow) => DraftRow): DraftState {
  return { ...state, rows: state.rows.map((r) => (r.key === key ? fn(r) : r)) };
}

/**
 * withTrailingBlank keeps an empty row at the END, the one the „Nový člen"
 * form types into: typing into it appends the next one, so filling the roster
 * is continuous typing. Blank rows are ignored by validation, dirty tracking
 * and the submit (see isBlankNewRow).
 *
 * A blank row anywhere ELSE is left where it is, never collapsed into one:
 * it is a person somebody is in the middle of editing (the form's row typed
 * and erased again, or a listed person emptied out), and dropping or
 * re-keying it would unmount the input under the cursor. The roster drops a
 * person left empty once they are closed.
 *
 * So the LAST row is always the free blank and never anything else, and the
 * roster relies on exactly that: a blank row that is not last is somebody
 * being edited. Nothing may therefore be appended BEHIND the end blank
 * (staged transfers go in front of it, see beforeTrailingBlank), and a blank
 * is never moved, since moving one would turn an emptied person back into
 * the free row.
 */
function withTrailingBlank(state: DraftState): DraftState {
  const last = state.rows.at(-1);
  if (last && isBlankNewRow(last)) return state;
  return {
    ...state,
    nextNewId: state.nextNewId + 1,
    rows: [...state.rows, blankRow(state.nextNewId)],
  };
}



/** Appends rows in front of the free blank row at the end, if there is one. */
function beforeTrailingBlank(rows: DraftRow[], added: DraftRow[]): DraftRow[] {
  const last = rows.at(-1);
  if (last && isBlankNewRow(last)) return [...rows.slice(0, -1), ...added, last];
  return [...rows, ...added];
}

export function draftReducer(state: DraftState, action: DraftAction): DraftState {
  return withTrailingBlank(baseReducer(state, action));
}

function baseReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case "HYDRATE": {
      // Transfers and new rows the user already staged are kept: hydration
      // refreshes what the server owns, it does not discard their work.
      const staged = state.rows.filter((r) => r.origin.kind !== "persisted");
      return {
        ...state,
        rows: [...action.members.map((m) => persistedRow(m, action.leaderPersonId)), ...staged],
        hydrated: true,
        leaderPersonId: action.leaderPersonId,
        rosterSignature: rosterSignature(action.members),
        formMessage: null,
      };
    }
    case "SET_LEADER":
      // The team query can resolve after the members query, so the first
      // HYDRATE may run with no leader id. Re-marking the rows is surgical —
      // it discards no typing — where a re-hydrate could.
      return {
        ...state,
        leaderPersonId: action.leaderPersonId,
        rows: state.rows.map((r) => {
          const personID =
            r.origin.kind === "persisted" || r.origin.kind === "transfer"
              ? r.origin.personId
              : null;
          const isLeader = personID != null && personID === action.leaderPersonId;
          return isLeader === r.isLeader ? r : { ...r, isLeader };
        }),
      };
    case "SET_SOURCE_SEASON":
      // Switching source season drops the transfer rows chosen from the old
      // one; edits to persisted or brand-new rows survive.
      return {
        ...state,
        sourceSeasonId: action.seasonId,
        rows: state.rows.filter((r) => !isSourceTransfer(r)),
      };
    case "TOGGLE_TRANSFER": {
      const key = `p:${action.membership.person.id}`;
      if (state.rows.some((r) => r.key === key)) {
        return { ...state, rows: state.rows.filter((r) => r.key !== key) };
      }
      return {
        ...state,
        rows: beforeTrailingBlank(state.rows, [transferRow(action.membership, action.leaderPersonId)]),
      };
    }
    case "SET_ALL_TRANSFERS": {
      const rest = state.rows.filter((r) => !isSourceTransfer(r));
      if (!action.on) return { ...state, rows: rest };
      return {
        ...state,
        rows: beforeTrailingBlank(
          rest,
          action.candidates.map((m) => transferRow(m, action.leaderPersonId)),
        ),
      };
    }
    case "RESET":
      // What HYDRATE deliberately keeps — staged transfers and new rows — is
      // exactly what a committed submit has to let go of: those people are the
      // server's now. Keeping them would show every newly registered member
      // twice and hold the draft dirty, which in turn blocks HYDRATE from
      // loading the soupiska that was just written.
      //
      // nextNewId survives so a freed key is never reused (see the row key
      // contract in draftTypes), and so does the chosen source season.
      // hydrated does NOT: the whole point of a reset is that the next
      // roster fetch re-hydrates.
      return { ...initialDraft, sourceSeasonId: state.sourceSeasonId, nextNewId: state.nextNewId };
    case "SET_CELL":
      return mapRow(state, action.key, (r) => ({
        ...r,
        person: { ...r.person, [action.field]: action.value } as typeof r.person,
        serverErrors: withoutError(r.serverErrors, `person.${action.field}` as DraftFieldPath),
        // Typing into a contested field is an answer to it.
        conflicts: withoutConflict(r.conflicts, action.field),
      }));
    case "SET_ROLES":
      return mapRow(state, action.key, (r) => ({
        ...r,
        roles: action.roles,
        serverErrors: withoutError(r.serverErrors, "roles"),
      }));
    case "TOGGLE_REMOVE":
      return mapRow(state, action.key, (r) =>
        // A staged-in row is dropped outright; only a persisted row can be
        // "removed", because only it exists server-side to close. Removal
        // also cancels the row's server errors: the fields they were bound
        // to are not being submitted any more, and with the inputs disabled
        // the user has no way to clear them — they blocked the submit with
        // nothing visibly wrong.
        // Conflicts go too, for the same reason: a removal sends no fields.
        r.origin.kind === "persisted" ? { ...r, removed: !r.removed, serverErrors: {}, conflicts: {} } : r,
      );
    case "DROP_STAGED":
      // A staged row has nothing server-side to close, so discarding it is
      // simply forgetting it — unlike TOGGLE_REMOVE, which stages a closure.
      return { ...state, rows: state.rows.filter((r) => r.key !== action.key) };
    case "APPLY_SERVER_ERRORS": {
      const notices: RebaseNotice[] = [];
      const rows = state.rows.map((r) => {
        const { row, tookTheirs } = withServerConflicts(
          { ...r, serverErrors: action.byRow.get(r.key) ?? {} },
          action.conflicts?.get(r.key),
          action.canEditIdentity ?? false,
        );
        if (tookTheirs.length > 0) {
          const name = `${row.person.last_name} ${row.person.first_name}`.trim();
          notices.push({
            key: row.key,
            message: `${name}: ${tookTheirs.map((f) => FIELD_LABELS[f]).join(", ")} mezitím vyplnil správce členské základny. Platí uložená hodnota, vaši už změnit nelze.`,
          });
        }
        return row;
      });
      return {
        ...state,
        formMessage: action.message,
        rows,
        rebaseNotices: notices.length > 0 ? notices : state.rebaseNotices,
      };
    }
    case "CLEAR_FORM_MESSAGE":
      return { ...state, formMessage: null };
    case "REBASE":
      return rebaseDraft(state, action);
    case "RESTORE": {
      const saved: DraftState = {
        ...initialDraft,
        rows: restoredRows(action.payload),
        sourceSeasonId: action.payload.source_season_id,
        // Never hand out a key the saved draft used, nor one this session did.
        nextNewId: Math.max(state.nextNewId, action.payload.next_new_id),
        hydrated: true,
      };
      return rebaseDraft(saved, { ...action, known: action.payload.known_membership_ids });
    }
    case "RESOLVE_CONFLICT":
      return mapRow(state, action.key, (r) => {
        const c = r.conflicts[action.field];
        if (!c) return r;
        return {
          ...r,
          person: action.take === "theirs" ? ({ ...r.person, [action.field]: c.theirs } as typeof r.person) : r.person,
          conflicts: withoutConflict(r.conflicts, action.field),
          serverErrors: withoutError(r.serverErrors, `person.${action.field}` as DraftFieldPath),
        };
      });
    case "REREGISTER": {
      const orphan = state.rows.find((r) => r.key === action.key);
      if (!orphan || orphan.origin.kind !== "persisted" || !orphan.orphaned) return state;
      // The person is a transfer candidate again once removed, so the same
      // person may also be staged from the panel. The orphan carries the
      // leader's edits; the other staged row goes, or two rows share a key.
      const transferKey = `p:${orphan.origin.personId}`;
      return {
        ...state,
        rows: state.rows.filter((r) => r.key !== transferKey).map((r) =>
          r.key === action.key && r.origin.kind === "persisted" && r.orphaned
            ? {
                ...r,
                // The transfer key, so the transfer panel sees them as staged.
                key: `p:${r.origin.personId}`,
                origin: {
                  kind: "transfer",
                  personId: r.origin.personId,
                  sourceSeasonId: action.seasonId,
                  reregistered: true,
                },
                orphaned: false,
                removed: false,
              }
            : r,
        ),
      };
    }
    case "MERGE_DUPLICATE":
      return mergeDuplicate(state, action.key, action.canEditIdentity);
    case "KEEP_SEPARATE":
      return mapRow(state, action.key, (r) => ({ ...r, duplicateOf: null }));
    case "CLEAR_REBASE_NOTICES":
      return { ...state, rebaseNotices: [] };
    default:
      return state;
  }
}
